/**
 * Tour track definitions used by ProductTour (task #485).
 *
 * Each track is a persona-narrated walkthrough that drives the visitor
 * through a sequence of real screens. The tour engine navigates via wouter
 * `setLocation(step.route)` between steps so the visitor sees the actual
 * product, not a slide deck.
 *
 * Selectors should resolve to a `data-tour=...` attribute on the page —
 * if the selector doesn't match, the engine still shows the tooltip
 * centered on screen (see ProductTour.tsx).
 *
 * Per task #485 acceptance criteria:
 *   - Quick tour preserves the original 6-step product overview.
 *   - Each persona track (Legal, Finance/AD, Admin) covers 6–10 real
 *     screens — enough to tell the persona's story end-to-end.
 */

export type TrackId = "quick" | "legal" | "finance" | "admin";

export interface TourStep {
  selector: string;
  title: string;
  body: string;
  route?: string;
  placement?: "bottom" | "top" | "right" | "left";
}

export interface TourTrack {
  id: TrackId;
  name: string;
  persona: string;
  description: string;
  estMinutes: number;
  steps: TourStep[];
}

const QUICK_STEPS: TourStep[] = [
  {
    selector: "[data-tour='dashboard-stats']",
    title: "Your dashboard at a glance",
    body: "Active contracts, total obligations, and compliance health update in real time.",
    route: "/dashboard",
    placement: "bottom",
  },
  {
    selector: "[data-tour='nav-contracts']",
    title: "Contracts",
    body: "Track every coach contract — terms, salary, expiry, and full audit history.",
    route: "/dashboard",
    placement: "right",
  },
  {
    selector: "[data-tour='nav-coaches']",
    title: "Coaches",
    body: "Profiles for every coach on staff, linked to their active contract and insurance.",
    route: "/dashboard",
    placement: "right",
  },
  {
    selector: "[data-tour='nav-renewals']",
    title: "Renewals pipeline",
    body: "See contracts coming up for renewal and move them through your workflow.",
    route: "/dashboard",
    placement: "right",
  },
  {
    selector: "[data-tour='nav-insurance']",
    title: "Insurance",
    body: "Track every coach's insurance coverage with expiry alerts so you never get caught uncovered.",
    route: "/dashboard",
    placement: "right",
  },
  {
    selector: "[data-tour='command-palette']",
    title: "Search anything with ⌘K",
    body: "Press ⌘K (or Ctrl+K) anywhere to jump to a coach, contract, or page.",
    route: "/dashboard",
    placement: "bottom",
  },
];

const LEGAL_STEPS: TourStep[] = [
  {
    selector: "[data-tour='dashboard-stats']",
    title: "Welcome, Counsel",
    body: "Start at the dashboard for an at-a-glance compliance read across the whole department.",
    route: "/dashboard",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Clause Library",
    body: "Versioned clauses with merge fields keep every contract aligned with current legal language. Edit once, push everywhere.",
    route: "/clause-library",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Contracts list",
    body: "Every active and expired contract, filterable by status, sport, or risk flag. Click any row to drill into clauses, parties, and history.",
    route: "/contracts",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Negotiations",
    body: "Track counters, comments, and round-by-round movement on every open deal — no more email scavenger hunts.",
    route: "/negotiations",
    placement: "bottom",
  },
  // Task #535: deep-link into the live negotiation showcase. The demo
  // seeder always populates three contracts in active negotiation states
  // (out_for_negotiation / counter_received / legal_review), so this stop
  // lands on a populated workspace — Carlos Reyes's mid-cycle deal with
  // a counter-offer just sent — instead of an empty list. Filter is
  // honoured by the contracts list page via the existing `lifecycle`
  // query param.
  {
    selector: "body",
    title: "Inside an active negotiation",
    body: "Open Carlos Reyes — DC contract: round 2 just went out with a counter on buyout schedule and signing bonus. Click the contract to see the redline diff, comment thread, and item-by-item negotiation tracker.",
    route: "/contracts?lifecycle=counter_received",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Approvals routing",
    body: "Persona-aware approval rules ensure Legal sees what Legal needs to see — no rubber-stamping, no missed sign-offs.",
    route: "/approvals",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Compensation verification",
    body: "Codify your standard playbook — minimum buyout, required clauses, mandatory rider language. Compliance is checked on every contract.",
    route: "/compensation-verification",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Audit log",
    body: "Every create, edit, status change, upload, and signature is captured with actor, timestamp, and persona — board- and auditor-ready.",
    route: "/audit-log",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Notifications",
    body: "Lifecycle reminders, expiring policies, missing signatures — pushed to email and the in-app notification center on a schedule you control.",
    route: "/notifications",
    placement: "bottom",
  },
];

const FINANCE_STEPS: TourStep[] = [
  {
    selector: "[data-tour='dashboard-stats']",
    title: "Welcome, Finance / AD",
    body: "Top-line obligations, renewals due, and compliance — your weekly briefing in one place.",
    route: "/dashboard",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Finance dashboard",
    body: "Total spend, per-program commitments, and forward-year obligations roll up automatically as contracts move.",
    route: "/finance",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Reports",
    body: "Board-ready obligations and renewal reports in two clicks — PDF or CSV. Designed to drop straight into a board packet.",
    route: "/reports",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Renewals pipeline",
    body: "See what's coming up before it expires. Move deals through your renewal workflow without surprises.",
    route: "/renewals",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Equity & compensation",
    body: "Compare across staff, programs, and tenure to keep compensation defensible and Title IX-ready.",
    route: "/equity",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Mitigation language",
    body: "Forward salary mitigation, offset clauses, and liquidated-damages tracking — so terminations and resignations don't blow up the budget.",
    route: "/mitigations",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Insurance coverage",
    body: "Track every policy linked to coaches and contracts so you know coverage limits before an incident — not after.",
    route: "/insurance",
    placement: "bottom",
  },
];

const ADMIN_STEPS: TourStep[] = [
  {
    selector: "[data-tour='dashboard-stats']",
    title: "Welcome, Admin",
    body: "Let's get your workspace running. The dashboard is the home for everyone on your team.",
    route: "/dashboard",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Add your roster",
    body: "Coaches are the unit of work. Add or import them, then attach contracts, insurance, and reviews.",
    route: "/coaches",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Import contracts",
    body: "Upload existing contract PDFs and our AI extraction pulls out terms, salary, and dates — no retyping.",
    route: "/contracts/import",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Clause Library setup",
    body: "Configure your standard clauses once. They'll be available with merge fields on every new contract.",
    route: "/clause-library",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Team & personas",
    body: "Invite Finance, HR, Legal, AD — each person picks personas that shape the UI and the audit log.",
    route: "/settings",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Approval rules",
    body: "Decide who has to sign off on what. Salary thresholds, contract types, and risk tiers all route appropriately.",
    route: "/approvals",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Notifications & reminders",
    body: "Set who gets notified when, and how often. Persona-aware rules keep noise low while critical deadlines stay loud.",
    route: "/notifications",
    placement: "bottom",
  },
  {
    selector: "body",
    title: "Billing & plan",
    body: "View your seat usage, invoices, and plan — upgrade or change cadence whenever you need.",
    route: "/settings",
    placement: "bottom",
  },
];

export const TOUR_TRACKS: Record<TrackId, TourTrack> = {
  quick: {
    id: "quick",
    name: "Quick tour",
    persona: "Anyone",
    description: "6-step product overview.",
    estMinutes: 2,
    steps: QUICK_STEPS,
  },
  legal: {
    id: "legal",
    name: "Legal tour",
    persona: "Legal / GC",
    description: "Clauses, Contracts, Negotiations, Approvals, Policy, Audit, Notifications.",
    estMinutes: 4,
    steps: LEGAL_STEPS,
  },
  finance: {
    id: "finance",
    name: "Finance / AD tour",
    persona: "Finance · AD",
    description: "Obligations, Reports, Renewals, Equity, Mitigation, Insurance.",
    estMinutes: 4,
    steps: FINANCE_STEPS,
  },
  admin: {
    id: "admin",
    name: "Admin onboarding",
    persona: "New admin",
    description: "Roster, Imports, Clauses, Team, Approvals, Notifications, Billing.",
    estMinutes: 5,
    steps: ADMIN_STEPS,
  },
};

export const TRACK_ORDER: TrackId[] = ["quick", "legal", "finance", "admin"];
